import { memo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssistantResponse } from 'types';
import {
  Disclaimer,
  DropdownArrow,
  Input,
  InputArea,
  MenuOptionContent,
  MenuOptionDesc,
  MenuOptionTitle,
  ModeLabel,
  ModeSelect,
  ModeSelectorWrapper,
  SendButton,
  StyledMenu,
  StyledMenuItem,
  Wrapper,
} from './Footer.styles';

interface Props {
  assistants: AssistantResponse[];
  selectedAssistantId: string | null;
  onAssistantChange: (id: string | null) => void;
  input: string;
  onInputChange: (val: string) => void;
  sending: boolean;
  onSend: () => void;
}

const FooterBase = ({ assistants, selectedAssistantId, onAssistantChange, input, onInputChange, sending, onSend }: Props) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectAssistant = (id: string) => {
    onAssistantChange(id);
    handleMenuClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSend();
  };

  const selectedAssistant = assistants.find((a) => a.id === selectedAssistantId);

  return (
    <Wrapper>
      {assistants.length > 0 && (
        <ModeSelectorWrapper>
          <ModeLabel>{t('user.chat.assistantLabel')}</ModeLabel>

          <div>
            <ModeSelect
              onClick={handleMenuClick}
              aria-label={t('user.chat.assistantLabel')}
              aria-controls={openMenu ? 'assistant-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openMenu ? 'true' : undefined}>
              <span>{selectedAssistant?.label}</span>
              <DropdownArrow isOpen={openMenu}>▲</DropdownArrow>
            </ModeSelect>

            <StyledMenu
              id="assistant-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              {assistants.map((assistant) => (
                <StyledMenuItem
                  key={assistant.id}
                  isActive={selectedAssistantId === assistant.id}
                  onClick={() => handleSelectAssistant(assistant.id)}>
                  <MenuOptionContent>
                    <MenuOptionTitle>{assistant.label}</MenuOptionTitle>
                    {assistant.description !== null && <MenuOptionDesc>{assistant.description}</MenuOptionDesc>}
                  </MenuOptionContent>
                </StyledMenuItem>
              ))}
            </StyledMenu>
          </div>
        </ModeSelectorWrapper>
      )}

      <InputArea>
        <Input
          type="text"
          placeholder={sending ? t('user.chat.thinking') : t('user.chat.inputPlaceholder')}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <SendButton onClick={onSend} disabled={sending} title={sending ? t('user.chat.sendingTitle') : t('user.chat.sendTitle')}>
          ➤
        </SendButton>
      </InputArea>

      <Disclaimer>{t('user.chat.aiDisclaimer')}</Disclaimer>
    </Wrapper>
  );
};

export const Footer = memo(FooterBase);
